import React from 'react';
import { Users, AlertCircle, HelpCircle, FileText, Bug, TrendingUp, TrendingDown, Minus, Ban, Volume2, Unlink, GitBranch } from 'lucide-react';
import type { QAIssueSeverity } from '@/services/kbService';

// ============================================================================
// Utility Functions - Safe Array Handling
// ============================================================================

export function ensureArray<T>(value: unknown): T[] {
  if (Array.isArray(value)) {
    return value as T[];
  }
  return [];
}

export function safeObjectEntries(obj: unknown): [string, unknown][] {
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    return Object.entries(obj);
  }
  return [];
}

export function safeObjectKeys(obj: unknown): string[] {
  if (obj && typeof obj === 'object' && !Array.isArray(obj)) {
    return Object.keys(obj);
  }
  return [];
}

// ============================================================================
// Helper Functions
// ============================================================================

export const severityOrder: Record<QAIssueSeverity, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
};

export const getSeverityBadgeClasses = (severity: string): string => {
  switch (severity) {
    case 'critical': return 'bg-pink-500/15 text-pink-400 border-pink-500/30';
    case 'high': return 'bg-destructive/10 text-red-400 border-destructive/30';
    case 'medium': return 'bg-amber-500/10 text-amber-500 border-amber-500/30';
    case 'low': return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/30';
    default: return 'bg-secondary text-muted-foreground border-border';
  }
};

export const getIssueTypeIcon = (type: string): React.ReactNode => {
  const iconProps = { className: "h-4 w-4" };
  switch (type) {
    case 'duplicate': return React.createElement(Users, iconProps);
    case 'inconsistent': return React.createElement(AlertCircle, iconProps);
    case 'low_confidence': return React.createElement(HelpCircle, iconProps);
    case 'missing_context': return React.createElement(FileText, iconProps);
    case 'invalid_format': return React.createElement(Bug, iconProps);
    case 'anomaly': return React.createElement(AlertCircle, iconProps);
    case 'misclassification': return React.createElement(AlertCircle, iconProps);
    case 'invalid_entry': return React.createElement(Ban, iconProps);
    case 'noise': return React.createElement(Volume2, iconProps);
    case 'orphan': return React.createElement(Unlink, iconProps);
    case 'invalid_relationship': return React.createElement(GitBranch, iconProps);
    default: return React.createElement(Bug, iconProps);
  }
};

export const getQualityScoreStatus = (score: number): { color: string; icon: React.ReactNode } => {
  const iconProps = { className: "h-5 w-5" };
  if (score >= 90) return { color: 'text-emerald-400', icon: React.createElement(TrendingUp, iconProps) };
  if (score >= 70) return { color: 'text-amber-500', icon: React.createElement(Minus, iconProps) };
  return { color: 'text-red-400', icon: React.createElement(TrendingDown, iconProps) };
};

export const formatDate = (dateString?: string): string => {
  if (!dateString) return 'N/A';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return 'Invalid date';
    return date.toLocaleDateString('en-GB', {
      day: '2-digit',
      month: 'short',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  } catch {
    return 'Invalid date';
  }
};

// ============================================================================
// Types
// ============================================================================

export type ActiveTab = 'issues' | 'clusters' | 'canonical' | 'rules' | 'vector-health' | 'settings';

export interface PaginationState {
  current: number;
  pageSize: number;
}
